import { Address } from "../components/form-components/AddressForm";
import { Authorization } from "../components/form-components/AuthorizationForm";
import { ConvictedOfCrime } from "../components/form-components/ConvictedOfCrimeForm";
import { Education } from "../components/form-components/EducationForm";
import { LegalName } from "../components/form-components/LegalNamesForm";
import { PersonalInformation } from "../components/form-components/PersonalInformationForm";
import { ClientResponseFile } from "../components/order-components/DownloadClientResponse";
import { OrderResponseFile } from "../components/order-components/DownloadGeneralOrderDocs";
import { CandidateOrder } from "../interfaces/candidate";
import { SimpleClient } from "../interfaces/client";
import { CriminalOrder } from "../interfaces/criminalOrder";
import { FullOrder } from "../interfaces/fullOrder";
import {
  CandidateCreateOrder,
  CloseOrderType,
  Order,
} from "../interfaces/order";
import { OrderStatus } from "../interfaces/orderStatus";
import { ReferenceOrder } from "../interfaces/referenceOrder";
import { PagedOrdersResponse, SimpleOrder } from "../interfaces/simpleOrder";
import { getAdditionalNamesByOrderID } from "./additionalNameService";
import { getCriminalDetailsByCriminalOrderID } from "./criminalDetailService";
import { getCriminalOrderByOrderID } from "./criminalOrderService";
import { fetchWithAuth } from "./fetchWithAuth";
import { convertBase64ToBlob, convertFileToBase64 } from "./helpers";
import { getOrderFormsByOrderID, getOrderFormTypes } from "./orderFormService";
import { getOrderServicesByOrderID } from "./orderServiceService";
import { getReferenceDetailsByReferenceOrderID } from "./referenceDetailService";
import { getReferenceOrderByOrderID } from "./referenceOrderService";
import { getAllServices } from "./serviceService";
import { getSubscriptionDetailsBySubscriptionOrderID } from "./subscriptionDetailService";
import { getSubscriptionOrderByOrderID } from "./subscriptionOrderService";

const APP_URL = process.env.REACT_APP_APP_URL || "";
const DEV_APP_URL = process.env.REACT_APP_DEV_APP_URL || "";
const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";

const siteBaseUrl =
  window.location.hostname === "localhost"
    ? APP_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_APP_URL
      : DEV_APP_URL;

const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

// This gets partial complete orders. Ex: will return an order if it's status is 3, but a reference or criminal order on it has been completed
export const getAllCompletedOrdersByClientID = async (
  clientID: number,
): Promise<SimpleOrder[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const orderResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/GetAllCompletedOrdersByClientID?clientID=${clientID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!orderResponse.ok) {
    throw new Error("Failed to fetch orders");
  }

  const orders = await orderResponse.json();

  const ordersWithDates = orders.map((order: Order) => ({
    ...order,
    initiatedDate: new Date(order.initiatedDate),
    receivedDate: order.receivedDate ? new Date(order.receivedDate) : null,
    candidateDOB: order.candidateDOB ? new Date(order.candidateDOB) : null,
    completedDate: order.completedDate ? new Date(order.completedDate) : null,
  }));

  return ordersWithDates;
};

export const getOrderByID = async (orderID: number): Promise<Order> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const orderResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/GetOrderByID/${orderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!orderResponse.ok) {
    throw new Error("Failed to fetch orders");
  }

  const order = await orderResponse.json();

  return order;
};

export const getOrderBySuranID = async (suranID: string): Promise<Order> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const orderResponse = await fetch(
    `${apiBaseUrl}/api/Order/GetOrderBySuranID/${suranID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!orderResponse.ok) {
    throw new Error("Failed to fetch orders");
  }

  const order = await orderResponse.json();

  return order;
};

export const checkIfOrderExists = async (
  suranID: string,
): Promise<Order | null> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  try {
    const orderResponse = await fetch(
      `${apiBaseUrl}/api/Order/CheckIfOrderExists/${suranID}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!orderResponse.ok) {
      console.warn("Unexpected error fetching order.");
      return null;
    }

    const order: Order | null = await orderResponse.json();
    return order;
  } catch (error) {
    return null;
  }
};
export const getSimpleOrderByID = async (
  orderID: number,
): Promise<SimpleOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const orderResponse = await fetch(
    `${apiBaseUrl}/api/Order/GetSimpleOrderByID/${orderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!orderResponse.ok) {
    throw new Error("Failed to fetch orders");
  }

  const order = await orderResponse.json();

  return order;
};

export const getFullOrder = async (orderID: number): Promise<FullOrder> => {
  if (isNaN(orderID)) {
    throw new Error("Invalid order ID");
  }

  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const fullOrderResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/GetFullOrder/${orderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!fullOrderResponse.ok) {
    throw new Error("Failed to fetch order");
  }

  const fullOrder = await fullOrderResponse.json();

  return fullOrder;
};

export const loadOrderData = async (orderId: number) => {
  if (isNaN(orderId)) {
    throw new Error("Invalid order ID");
  }

  const order = await getOrderByID(orderId);
  const additionalNames = await getAdditionalNamesByOrderID(order.orderID);
  const orderForms = (await getOrderFormsByOrderID(orderId)) || [];
  const orderFormTypes = (await getOrderFormTypes()) || [];
  const criminalOrder = await getCriminalOrderByOrderID(order.orderID);
  var criminalDetails =
    (await getCriminalDetailsByCriminalOrderID(
      criminalOrder.criminalOrderID,
    )) || [];
  const visibleSubCriminalDetails = criminalDetails.filter(
    (cd) => cd.subscriptionAddOn && !cd.inActive && cd.adminReviewed,
  );
  criminalDetails = criminalDetails.filter(
    (cd) => !cd.subscriptionAddOn && !cd.inActive,
  );
  const subscriptionOrder = await getSubscriptionOrderByOrderID(order.orderID);
  var subscriptionDetails =
    (await getSubscriptionDetailsBySubscriptionOrderID(
      subscriptionOrder.subscriptionOrderID,
    )) || [];
  const referenceOrder = await getReferenceOrderByOrderID(order.orderID);
  const referenceDetails =
    (await getReferenceDetailsByReferenceOrderID(
      referenceOrder.referenceOrderID,
    )) || [];
  const services = (await getAllServices()) || [];
  const orderServices = (await getOrderServicesByOrderID(order.orderID)) || [];

  return {
    order,
    additionalNames,
    orderForms,
    orderFormTypes,
    criminalOrder,
    criminalDetails,
    visibleSubCriminalDetails,
    subscriptionOrder,
    subscriptionDetails,
    referenceOrder,
    referenceDetails,
    services,
    orderServices,
  };
};

export const getAllOrderStatuses = async (): Promise<OrderStatus[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const orderStatusesResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/OrderStatus/GetAllOrderStatuses`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!orderStatusesResponse.ok) {
    throw new Error("Failed to fetch orders");
  }

  const orderStatuses = await orderStatusesResponse.json();

  return orderStatuses;
};

export const createOrderCandidate = async (
  candidateCreateOrder: CandidateCreateOrder,
): Promise<Order> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(`${apiBaseUrl}/api/Order/CreateOrderCandidate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(candidateCreateOrder),
  });

  if (!response.ok) {
    throw new Error("Failed to create order.");
  }
  return response.json();
};

export const createOrdersAdmin = async (
  client: SimpleClient,
  order: Order,
  candidateOrders: CandidateOrder[],
  services: number[],
  referenceOrder?: ReferenceOrder,
  criminalOrder?: CriminalOrder,
): Promise<any> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const formData = {
    client,
    order,
    candidateOrders,
    services,
    referenceOrder,
    criminalOrder,
  };

  const response = await fetch(`${apiBaseUrl}/api/Order/CreateOrdersAdmin`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    throw new Error("Failed to create orders.");
  }
  return response.json();
};

export const getAllOrdersByCandidateID = async (
  candidateID: number,
): Promise<Order[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const orderResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/GetAllOrdersByCandidateID/${candidateID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!orderResponse.ok) {
    throw new Error("Failed to fetch orders");
  }

  const orders = await orderResponse.json();

  return orders;
};

export const updateOrderStatus = async (orderID: number, statusID: number) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/Order/UpdateOrderStatus/${orderID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ statusID }),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to update order status");
  }

  return response.status === 204 ? null : await response.json();
};

export const checkOrderingAvailability = async (
  abbreviation: string,
  candidateID: number,
): Promise<boolean> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const data = { abbreviation, candidateID };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Order/CheckOrderingAvailability`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    const result = await response.json();

    return result === true;
  } catch (error) {
    console.error("Error checking ordering availability:", error);
    return false; // Return false in case of an error to block creating the order
  }
};

export const getOrdersWithParams = async (
  orderStatusType: number,
  pageNumber = 1,
  pageSize = 10,
  searchTerm = "",
  startDate: string | null = null,
  endDate: string | null = null,
  orderType: string | null = null,
  searchType: string | null = null,
  sortMode: "updated" | "lastName" = "updated",
): Promise<PagedOrdersResponse> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const params = new URLSearchParams({
    orderStatusType: orderStatusType.toString(),
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
    searchTerm,
  });

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (orderType) params.append("orderType", orderType);
  if (searchType) params.append("searchType", searchType);
  if (sortMode) params.append("sortMode", sortMode);

  const endpoint =
    orderStatusType === 0 || orderStatusType === 4
      ? "GetOrdersWithParams"
      : "GetSmallerOrdersWithParams";

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/${endpoint}?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch orders");
  }

  const data: PagedOrdersResponse = await response.json();
  if (!data || !Array.isArray(data.orders)) {
    throw new Error("Invalid response structure");
  }

  return data;
};

export const getAllOrdersByClientIDWithParams = async (
  orderStatus: string,
  searchTerm: string,
  clientID: number,
  pageNumber = 1,
  pageSize = 10,
): Promise<PagedOrdersResponse> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const params = new URLSearchParams({
    orderStatus: orderStatus,
    searchTerm,
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
  });

  if (clientID) params.append("clientID", clientID.toString());

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/GetOrdersWithParamsByClientID?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch orders");
  }

  const data: PagedOrdersResponse = await response.json();
  if (!data || !Array.isArray(data.orders)) {
    throw new Error("Invalid response structure");
  }

  return data;
};

export const updateOrder = async (order: Order): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Order/UpdateOrder`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(order),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update the order.");
    }
  } catch (error) {
    console.error("Error updating order:", error);
    throw error;
  }
};

export const updateOrderPaymentStatus = async (
  orderID: number,
  status: boolean,
) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/UpdateOrderPaymentStatus/${orderID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(status),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to update order payment status");
  }

  return response.status === 204 ? null : await response.json();
};

export const updateOrderPaymentStatusBySuranID = async (
  suranID: string,
  orderID: number,
  status: boolean,
): Promise<Order> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/Order/UpdateOrderPaymentStatusBySuranID/${suranID}/${orderID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(status),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to update order payment status");
  }

  return await response.json();
};

export const deleteOrder = async (orderID: number, deleteMessage: string) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/DeleteOrder/${orderID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(deleteMessage),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to delete order");
  }

  return response.status === 204 ? null : await response.json();
};

export const hardDeleteOrder = async (orderID: number) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/HardDeleteOrder/${orderID}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to hard delete order");
  }

  return response.status === 204 ? null : await response.json();
};

export const sendNotifySuranEmailOrder = async (
  order: Order,
  client: SimpleClient,
): Promise<boolean> => {
  try {
    const formData = {
      order,
      client,
      baseUrl: siteBaseUrl,
    };

    const response = await fetch(
      `${apiBaseUrl}/api/Order/SendNotifySuranEmailOrder`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to send the email to notify suran.");
      return false;
    }

    const result = await response.json();

    if (result.success) {
      console.log("Suran notified successfully.");
      return true;
    } else {
      console.error("Error from server:", result.message);
      return false;
    }
  } catch (error) {
    console.error("Error sending the notification to suran.", error);
    return false;
  }
};

export const GetAllClientResponses = async (
  orderID: number,
): Promise<ClientResponseFile[]> => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/GetAllClientResponses?orderID=${orderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch client response files");
  }

  const files: ClientResponseFile[] = await response.json();
  return files;
};

export const uploadClientResponse = async (
  order: Order,
  file: File,
): Promise<boolean> => {
  try {
    const { base64Data, contentType } = await convertFileToBase64(file);

    const formData = {
      order,
      fileBase64: base64Data,
      contentType,
      fileName: file.name,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Order/UploadClientResponse`,

      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to upload client response.");
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error while uploading form:", error);
    return false;
  }
};

export const uploadGeneralOrderDocument = async (
  order: Order,
  file: File,
): Promise<boolean> => {
  try {
    const { base64Data, contentType } = await convertFileToBase64(file);

    const formData = {
      order,
      fileBase64: base64Data,
      contentType,
      fileName: file.name,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Order/UploadGeneralOrderDocument`,

      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to upload order document.");
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error while uploading form:", error);
    return false;
  }
};

export const GetAllGeneralOrderDocuments = async (
  orderID: number,
): Promise<ClientResponseFile[]> => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/GetAllGeneralOrderDocuments?orderID=${orderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch order document files");
  }

  const files: OrderResponseFile[] = await response.json();
  return files;
};

export const getDataForCloseOrderTable = async (): Promise<
  CloseOrderType[] | null
> => {
  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Order/GetDataForCloseOrderTable`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch close order table data");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching close order table data:", error);
    return null;
  }
};

export const downloadOrderResults = async (
  orders: SimpleOrder[],
): Promise<Blob | null> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Order/DownloadOrderResults`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orders),
      },
    );

    if (!response.ok) {
      console.error("Failed to generate the order results document.", response);
      return null;
    }

    const result = await response.json();

    if (result.success && result.csvBase64) {
      // Convert Base64 string to Blob
      const csvBlob = await convertBase64ToBlob(result.csvBase64, "text/csv");
      return csvBlob;
    } else {
      console.error("Error from server:", result.message);
      return null;
    }
  } catch (error) {
    console.error("Error generating the invoice report:", error);
    return null;
  }
};

export const createSaveOrderSuran = async (
  candidateCreateOrder: CandidateCreateOrder,
  personalInformation: PersonalInformation,
  convictedOfCrimes: ConvictedOfCrime[],
  legalNames: LegalName[],
  residentialAddresses: Address[],
  employmentAddresses: Address[],
  authorization: Authorization,
  criminalYears: number,
  diocese: string,
  hasEmploymentService: boolean,
  hasEducation: boolean,
  file?: File,
  educations?: Education[],
  oneEducation?: boolean,
  authorizationFCRA?: Authorization,
): Promise<Order> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  let base64Data = "";

  if (file) {
    ({ base64Data } = await convertFileToBase64(file));
  }

  const formData = {
    candidateCreateOrder,
    personalInformation,
    convictedOfCrimes,
    legalNames,
    residentialAddresses,
    employmentAddresses,
    authorization,
    criminalYears,
    diocese,
    hasEmploymentService,
    hasEducation,
    base64File: base64Data,
    educations,
    oneEducation,
    authorizationFCRA,
  };

  const response = await fetch(`${apiBaseUrl}/api/Order/CreateSaveOrderSuran`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    throw new Error("Failed to create order.");
  }
  return response.json();
};

export const evaluateOrderStatus = async (orderID: number): Promise<Order> => {
  if (!apiBaseUrl) throw new Error("API URL is not defined");

  const token = localStorage.getItem("token");
  if (!token) throw new Error("User is not authenticated");

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Order/EvaluateStatus/${orderID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Evaluate Order Status Error:", errorText);
    throw new Error("Failed to evaluate order status.");
  }

  return response.json();
};
